import { useEffect, useState } from "react";
import { Mic, MicOff, Phone, PhoneIncoming, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePhone } from "@/context/PhoneContext";
import { fmtDur } from "@/lib/api";

const DEVICE_LABEL = {
  not_configured: "Browser phone: Not configured", registering: "Browser phone: connecting…", ready: "Browser phone ready",
  offline: "Browser phone offline", error: "Browser phone error", mic_blocked: "Microphone blocked",
};

const Timer = ({ startedAt }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return <span data-testid="softphone-duration" className="font-mono tabular-nums">{startedAt ? fmtDur((Date.now() - startedAt) / 1000) : "0:00"}</span>;
};

export const Softphone = () => {
  const { deviceState, mic, checkMic, incoming, active, answer, reject, hangup, toggleMute } = usePhone();
  if (incoming) {
    return (
      <div data-testid="softphone-incoming" className="flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 pl-3 pr-1 py-1 animate-pulse">
        <PhoneIncoming className="h-4 w-4 text-blue-700" />
        <span className="text-sm font-medium text-blue-900 max-w-[160px] truncate">{incoming.info.name}</span>
        <Button size="sm" data-testid="softphone-answer-btn" className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={answer}>Answer</Button>
        <Button size="sm" data-testid="softphone-reject-btn" className="h-7 rounded-full bg-rose-600 hover:bg-rose-700" onClick={reject}>Reject</Button>
      </div>
    );
  }
  if (active) {
    return (
      <div data-testid="softphone-active" className="flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 pl-3 pr-1 py-1">
        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-sm font-medium text-emerald-900 max-w-[160px] truncate" data-testid="softphone-caller">{active.info?.name || "Call"}</span>
        <span className="text-xs text-emerald-800">{active.state === "live" ? <Timer startedAt={active.startedAt} /> : "connecting…"}</span>
        <Button size="icon" variant="outline" data-testid="softphone-mute-btn" className="h-7 w-7 rounded-full" onClick={toggleMute}>
          {active.muted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
        </Button>
        <Button size="icon" data-testid="softphone-hangup-btn" className="h-7 w-7 rounded-full bg-rose-600 hover:bg-rose-700" onClick={hangup}>
          <PhoneOff className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }
  return (
    <div className="hidden sm:flex items-center gap-2 text-xs text-slate-600" data-testid="softphone-idle">
      <Phone className={`h-4 w-4 ${deviceState === "ready" ? "text-emerald-600" : "text-slate-400"}`} />
      <span data-testid="softphone-device-state">{DEVICE_LABEL[deviceState]}</span>
      {deviceState !== "not_configured" && mic !== "ready" && (
        <button data-testid="softphone-enable-mic-btn" className="underline text-blue-700" onClick={checkMic}>
          {mic === "blocked" ? "Mic blocked — retry" : "Enable mic"}
        </button>
      )}
      {mic === "ready" && <span className="text-emerald-700" data-testid="softphone-mic-ready">Mic ready</span>}
    </div>
  );
};
